import type { ChatListUserItem, ChatListSystemItem } from '@whelk/const';
import {
  RePullMessageData,
  RePullMessageList,
} from '@whelk/hooks/src/ChatHooks/useTextSseDataHandle';
import {
  ChatMessage,
  TextSseSendData,
  ChatMessageMeta,
  PaintMessageMeta,
  SearchMessageMeta,
  TextSseMessageData,
  LoadingSseMessageData,
  TextSseUrlStatusData,
  SendMessageJsbData,
  OptionItem,
  SugOption,
} from '@whelk/services/src/type/chat';

export const getMsgIdBySideMsgId = (
  sideMsgId: string,
  chatList: Array<ChatListUserItem | ChatListSystemItem>,
) => {
  const sideMsgIndex = chatList.findIndex((item) => item.msgId === sideMsgId);
  const sideMsg = chatList[sideMsgIndex];
  const targetMsgIndex = sideMsg?.msgType === 'system' ? sideMsgIndex - 1 : sideMsgIndex + 1;
  const MsgId = chatList[targetMsgIndex]?.msgId;
  return MsgId;
};

export const manageMsgIds = (
  sideMsgId: string,
  msgIds: string[],
  chatList: Array<ChatListUserItem | ChatListSystemItem>,
) => {
  const newMessageIds = [...msgIds];
  const otherMsgId = getMsgIdBySideMsgId(sideMsgId, chatList);
  const sideMsgIndex = newMessageIds.findIndex((item) => item === sideMsgId);
  if (sideMsgIndex >= 0) {
    newMessageIds.splice(sideMsgIndex, 1);
    const otherMsgIndex = newMessageIds.findIndex((item) => item === otherMsgId);
    newMessageIds.splice(otherMsgIndex, 1);
  } else {
    newMessageIds.push(sideMsgId, otherMsgId);
  }
  return [...new Set(newMessageIds)];
};

export const isLoadingMeta = (
  messageData:
    | ChatMessage
    | ChatMessageMeta
    | RePullMessageList
    | SearchMessageMeta
    | PaintMessageMeta,
): messageData is SearchMessageMeta | PaintMessageMeta => {
  const status = (messageData as SearchMessageMeta)?.status;
  const statusCode = (messageData as SearchMessageMeta)?.statusCode;
  return !!(status && statusCode);
};

export const isSearchSseData = (
  data:
    | TextSseUrlStatusData
    | TextSseMessageData
    | LoadingSseMessageData
    | TextSseSendData
    | RePullMessageData,
): data is LoadingSseMessageData => {
  return !!(data as LoadingSseMessageData)?.data?.statusResult;
};

export const isPaintingMeta = (
  messageData:
    | ChatMessage
    | ChatMessageMeta
    | RePullMessageList
    | SearchMessageMeta
    | PaintMessageMeta,
): messageData is PaintMessageMeta => {
  const status = (messageData as SearchMessageMeta)?.status;
  const statusCode = (messageData as SearchMessageMeta)?.statusCode;
  return !!(statusCode === 102 && status);
};
export const isPaintSuccessMeta = (
  messageData:
    | ChatMessage
    | ChatMessageMeta
    | RePullMessageList
    | SearchMessageMeta
    | PaintMessageMeta,
): messageData is PaintMessageMeta => {
  const status = (messageData as SearchMessageMeta)?.status;
  const statusCode = (messageData as SearchMessageMeta)?.statusCode;
  return !!(statusCode === 202 && status);
};
export const isSearchMeta = (
  messageData:
    | ChatMessage
    | ChatMessageMeta
    | RePullMessageList
    | SearchMessageMeta
    | PaintMessageMeta,
): messageData is PaintMessageMeta => {
  const status = (messageData as SearchMessageMeta)?.status;
  const statusCode = (messageData as SearchMessageMeta)?.statusCode;
  return !!(statusCode === 101 && status);
};

export const isReadingMeta = (
  messageData:
    | ChatMessage
    | ChatMessageMeta
    | RePullMessageList
    | SearchMessageMeta
    | PaintMessageMeta,
): messageData is PaintMessageMeta => {
  const status = (messageData as SearchMessageMeta)?.status;
  const statusCode = (messageData as SearchMessageMeta)?.statusCode;
  return !!(statusCode === 110 && status);
};

export const isFinishMeta = (
  messageData:
    | ChatMessage
    | ChatMessageMeta
    | RePullMessageList
    | SearchMessageMeta
    | PaintMessageMeta,
): messageData is PaintMessageMeta => {
  const status = (messageData as SearchMessageMeta)?.status;
  const statusCode = (messageData as SearchMessageMeta)?.statusCode;
  return !!(statusCode === 201 && status);
};

/**
 * 卡片类型枚举
 */
export enum CardCodeEnum {
  SEARCHING = 101,
  READING = 110,
  PAINTING = 102,
  PAINT_SUCCESS = 202,
  SUCCESS = 201,
}

/**
 * 占位符模版替换数据
 * @param template 模版规则
 * @param templateData 替换的数据
 * @returns
 */
const parseTemplate = (template: string, templateData: { [key: string]: string }) => {
  const placeholderRegex = /\$(\d+|\w+)/g;
  const templateList = template?.split('\n');
  // 替换模板中的占位符
  const content = templateList
    ?.map((item: string) => {
      return item.replace(placeholderRegex, (match: string, p: string) => {
        // 兼容P是数字的场景
        return templateData[`$${p}`] || templateData[`${p}`] || 'null';
      });
    })
    ?.filter((item) => !item?.includes('null'));

  return content?.join('\n');
};

/**
 * 整理占位符数据对象
 * @param msgContent 用户输入的文本（包含语音场景）
 * @param selectionOptions sug选中的选项
 * @returns
 */
const getTemplateData = (msgContent: string, selectionOptions: OptionItem[] | undefined) => {
  const results = {
    input_box: msgContent,
  };

  selectionOptions?.map((item: OptionItem) => {
    const { optionID, optionValues } = item;
    // @ts-ignore 需要使用$作为属性值
    results[`$${optionID}`] = optionValues?.join(',');
  });

  return results;
};

/**
 * sug场景数据拼接
 * @param msgContent 用户输入的文本（包含语音场景）
 * @param bridgeOriginData sendMessage jsb bridge 输入的数据
 * @returns 模版替换后的文本
 */
export const sugOptionsSplicing = (msgContent = '', bridgeOriginData: SendMessageJsbData) => {
  // 无sug选项的数据 => 不处理
  if (!bridgeOriginData || !bridgeOriginData.sugOption) return msgContent;

  const { template = '', selectionOptions } = bridgeOriginData.sugOption;
  const templateData = getTemplateData(msgContent, selectionOptions);
  const sugContent = parseTemplate(template, templateData);

  console.log('sugOptionsSplicing', msgContent, bridgeOriginData, sugContent);
  // 兜底：端上数据发送字段错误，发不出消息的场景
  return sugContent || msgContent;
};

/**
 * 埋点参数整合：eventType动态字段
 * @param sugOption sug数据
 * @returns 参数
 */
export const getSugTrackParam = (sugOption?: SugOption) => {
  const results: {
    [key: string]: any;
  } = {};
  // 无sug选项的数据 => 不处理
  if (!sugOption) return results;

  const { demoName, selectionOptions } = sugOption;
  selectionOptions?.forEach((item: OptionItem) => {
    const { eventType = '', optionValues } = item;
    results[String(eventType)] = optionValues?.join('、');
  });
  return { ...results, demo_name: demoName };
};

export const hasThinkAndNotEmpty = (content: string) => {
  if (!content) {
    return false;
  }
  if (content.includes('<think>')) {
    return true;
  }
  return false;
};
